import React from 'react';
import { Block, Inline } from '@contentful/rich-text-types';
import type { Sys } from 'src/types/contentful-sdk-compat';
import { SpringValue } from '@react-spring/web';
import type { ITheme } from 'threads5/themes/theme';
import SxProps from 'threads5/themes/sx-props';

import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import Stack from 'src/components-v2/Layout/Stack/Stack';
import {
  Form,
  Label,
  Slider,
  MortgageTooltip,
  HelperText,
} from './MortgageCalculator.styled';
import { VisuallyHiddenContainer } from 'src/components-v2/DataDisplay/VisuallyHiddenContainer';
import { formatCurrency, formatNumber } from 'src/lib/utils';
import type { IUserInput } from './MortgageCalculator.types';

interface MortgageFormProps {
  annualMortgageApplicationsInputRef: React.RefObject<HTMLInputElement>;
  annualMortgageApplicationsSliderRef: React.RefObject<HTMLInputElement>;
  currentVoAConversionRateInputRef: React.RefObject<HTMLInputElement>;
  currentVoAConversionRateSliderRef: React.RefObject<HTMLInputElement>;
  averageVerificationCostPerApplicationInputRef: React.RefObject<HTMLInputElement>;
  averageVerificationCostPerApplicationSliderRef: React.RefObject<HTMLInputElement>;
  fields: {
    formBody?: Block | Inline;
    trackingContext?: Record<string, unknown>;
    styles?: SxProps<ITheme>;
  };
  formRef: React.RefObject<HTMLFormElement>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hasUserInteracted: React.MutableRefObject<boolean>;
  sys: Sys;
  userInput: IUserInput;
  annualMortgageApplicationsSliderValue: SpringValue<number>;
  currentVoAConversionRateSliderValue: SpringValue<number>;
  averageVerificationCostPerApplicationSliderValue: SpringValue<number>;
}

export const MortgageForm: React.FC<MortgageFormProps> = ({
  annualMortgageApplicationsInputRef,
  annualMortgageApplicationsSliderRef,
  currentVoAConversionRateInputRef,
  currentVoAConversionRateSliderRef,
  averageVerificationCostPerApplicationInputRef,
  averageVerificationCostPerApplicationSliderRef,
  annualMortgageApplicationsSliderValue,
  currentVoAConversionRateSliderValue,
  averageVerificationCostPerApplicationSliderValue,
  fields,
  formRef,
  handleChange,
  hasUserInteracted,
  sys,
  userInput,
}) => {
  return (
    <Stack
      className='cms-mortgage-calculator-form'
      gap={{ xs: 1.5, md: 2 }}
      justifyContent='center'
      sx={{ height: '100%', width: '100%' }}
    >
      <RichTextSwitch
        sys={sys}
        content={fields?.formBody}
        nodes={{
          h1: {
            variant: 'h3',
            component: 'h1',
            fieldId: 'formBody',
            sx: {
              fontSize: { xs: '2.4rem', sm: '4.0rem' },
            },
          },
          h2: {
            variant: 'h3',
            component: 'h2',
            fieldId: 'formBody',
            sx: {
              fontSize: { xs: '2.4rem', sm: '4.0rem' },
            },
          },
          p: {
            variant: 'p',
            component: 'p',
            fieldId: 'formBody',
            sx: {
              fontSize: { xs: '1.8rem', sm: '2.0rem' },
            },
          },
        }}
      />
      <Form ref={formRef} className='cms-mortgage-calculator-form-body'>
        <Stack
          className='cms-mortgage-calculator-form-field-label-row'
          direction={{ xs: 'column', md: 'row' }}
          gap={1}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Label
            as='span'
            className='cms-mortgage-calculator-form-label'
            sx={{ mb: '-1.2rem' }}
          >
            Annual mortgage applications
          </Label>
          <MortgageTooltip title='Total number of mortgage applications you process each year' />
        </Stack>

        <Stack
          className='cms-mortgage-calculator-form-field-row'
          direction='row'
          gap={3.75}
          alignItems={'center'}
        >
          <Label htmlFor='annual-mortgage-applications'>
            <VisuallyHiddenContainer>
              Annual mortgage applications
            </VisuallyHiddenContainer>
            <input
              ref={annualMortgageApplicationsInputRef}
              id='annual-mortgage-applications'
              name='annualMortgageApplications'
              onChange={handleChange}
              type='text'
              inputMode='numeric'
              value={formatNumber({
                value: userInput?.annualMortgageApplications,
              })}
            />
          </Label>
          <Slider
            className='cms-mortgage-calculator-form-slider'
            tabIndex={-1}
            isPulsing={!hasUserInteracted.current}
            max='25000'
            min='0'
            name='annualMortgageApplications'
            onChange={handleChange}
            onFocus={() => {
              annualMortgageApplicationsInputRef.current?.focus();
            }}
            onMouseUp={() => {
              annualMortgageApplicationsInputRef.current?.blur();
              annualMortgageApplicationsSliderRef.current?.blur();
            }}
            ref={annualMortgageApplicationsSliderRef}
            type='range'
            value={
              hasUserInteracted.current
                ? userInput.annualMortgageApplications
                : annualMortgageApplicationsSliderValue
            }
          />
        </Stack>
        <Stack
          className='cms-mortgage-calculator-form-field-label-row'
          direction={{ xs: 'column', md: 'row' }}
          gap={1}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Label
            as='span'
            className='cms-mortgage-calculator-form-label'
            sx={{ mb: '-1.2rem' }}
          >
            Current VoA conversion rate
          </Label>
          <MortgageTooltip title='Percentage of applicants who complete digital verification of assets' />
        </Stack>
        <Stack
          className='cms-mortgage-calculator-form-field-row'
          direction='row'
          gap={3.75}
          alignItems={'center'}
        >
          <Stack
            className='cms-mortgage-calculator-form-field-input-group'
            direction='column'
            gap={1}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Label htmlFor='current-voa-conversion-rate'>
              <VisuallyHiddenContainer>
                Current VoA conversion rate
              </VisuallyHiddenContainer>
              <input
                ref={currentVoAConversionRateInputRef}
                id='current-voa-conversion-rate'
                name='currentVoAConversionRate'
                onChange={handleChange}
                type='text'
                inputMode='numeric'
                value={`${userInput?.currentVoAConversionRate}%`}
              />
            </Label>
            <HelperText className='cms-mortgage-calculator-form-helper'>
              Industry average: 40%
            </HelperText>
          </Stack>
          <Slider
            className='cms-mortgage-calculator-form-slider'
            tabIndex={-1}
            max='100'
            min='0'
            name='currentVoAConversionRate'
            onChange={handleChange}
            type='range'
            ref={currentVoAConversionRateSliderRef}
            value={
              hasUserInteracted.current
                ? userInput.currentVoAConversionRate
                : currentVoAConversionRateSliderValue
            }
            onFocus={() => {
              currentVoAConversionRateInputRef.current?.focus();
            }}
            onMouseUp={() => {
              currentVoAConversionRateInputRef.current?.blur();
              currentVoAConversionRateSliderRef.current?.blur();
            }}
          />
        </Stack>
        <Stack
          className='cms-mortgage-calculator-form-field-label-row'
          direction={{ xs: 'column', md: 'row' }}
          gap={1}
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Label
            as='span'
            className='cms-mortgage-calculator-form-label'
            sx={{ mb: '-1.2rem' }}
          >
            Average verification cost per application
          </Label>
          <MortgageTooltip title='Your average cost per application for asset, income, or employment verification' />
        </Stack>

        <Stack
          className='cms-mortgage-calculator-form-field-row'
          direction='row'
          gap={3.75}
          alignItems={'center'}
        >
          <Stack
            className='cms-mortgage-calculator-form-field-input-group'
            direction='column'
            gap={1}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Label htmlFor='average-verification-cost-per-application'>
              <VisuallyHiddenContainer>
                Average verification cost per application
              </VisuallyHiddenContainer>
              <input
                ref={averageVerificationCostPerApplicationInputRef}
                id='average-verification-cost-per-application'
                name='averageVerificationCostPerApplication'
                onChange={handleChange}
                type='text'
                inputMode='numeric'
                value={formatCurrency({
                  value: userInput?.averageVerificationCostPerApplication,
                  showDecimals: false,
                })}
              />
            </Label>
            <HelperText className='cms-mortgage-calculator-form-helper'>
              Industry average: $200
            </HelperText>
          </Stack>
          <Slider
            className='cms-mortgage-calculator-form-slider'
            tabIndex={-1}
            max='1000'
            min='0'
            name='averageVerificationCostPerApplication'
            onChange={handleChange}
            type='range'
            value={
              hasUserInteracted.current
                ? userInput.averageVerificationCostPerApplication
                : averageVerificationCostPerApplicationSliderValue
            }
            ref={averageVerificationCostPerApplicationSliderRef}
            onFocus={() => {
              averageVerificationCostPerApplicationInputRef.current?.focus();
            }}
            onMouseUp={() => {
              averageVerificationCostPerApplicationInputRef.current?.blur();
              averageVerificationCostPerApplicationSliderRef.current?.blur();
            }}
          />
        </Stack>
      </Form>
    </Stack>
  );
};
